<template>
    <component
        :is="href ? 'a' : tag"
        class="z-caption"
        :class="classObject"
        :href="href"
        :style="styleObject"
    >
        <z-icon
            v-if="icon"
            :name="icon"
            class="z-caption__icon"
        />
        <span class="z-caption__text">
            <slot />
            <z-icon
                v-if="href"
                class="z-caption__arrow"
                name="arrows/round-arrow-small"
                dir="right"
            />
        </span>
    </component>
</template>

<script setup>
import {computed} from 'vue';

const props = defineProps({
    tag: {
        type: String,
        default: 'p',
        validator: prop => ['a', 'span', 'p', 'caption', 'figcaption', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(prop)
    },
    weight: {
        type: String,
        validator: prop => ['100', '200', '300', '400', '500', '600', '700', '800', '900'].includes(prop)
    },
    size: {
        type: String,
        default: 'xl',
        validator: prop => ['5xl', '4xl', '3xl', '2xl', 'xl', 'lt', 'l', 'm', 's', 'xs', '2xs'].includes(prop)
    },
    italic: {
        type: Boolean,
        default: false
    },
    uppercase: {
        type: Boolean,
        default: false
    },
    lowercase: {
        type: Boolean,
        default: false
    },
    icon: {
        type: String
    },
    fontFamily: {
        type: String,
        default: 'primary',
        validator: prop => ['primary', 'secondary', 'monospace'].includes(prop)
    },
    theme: {
        type: String,
        validator: prop => ['indent'].includes(prop)
    },
    borderTop: {
        type: Boolean,
        default: false
    },
    borderTopWidth: {
        type: Number,
        default: 10
    },
    borderBottom: {
        type: Boolean,
        default: false
    },
    borderBottomWidth: {
        type: Number,
        default: 4
    },
    href: [String, Boolean],
    bgImage: {
        type: String
    },
    bgIndent: {
        type: Number,
        default: 64
    },
    bgSize: {
        type: String,
        default: 'xl',
        validator: prop => ['xl', 'l', 'm'].includes(prop)
    },
    bgCentered: {
        type: Boolean,
        default: false
    }
})

const bgImageHeight = {
    xl: 200,
    l: 140,
    m: 80
}

const classObject = computed(() => {
    return ({
        'u-italic': props.italic,
        'u-uppercase': props.uppercase,
        'u-lowercase': props.lowercase,
        'z-caption--link': props.href,
        'z-caption--with-icon': props.icon,
        'z-caption--border-top': props.borderTop,
        'z-caption--border-bottom': props.borderBottom,
        'z-caption--bg': props.bgImage,
        [`u-${props.fontFamily}-font`]: props.fontFamily,
        'z-caption--bg-centered': props.bgCentered,
        [`z-caption--size-${props.size}`]: props.size,
        [`z-caption--weight-${props.weight}`]: props.weight,
        [`z-caption--theme-${props.theme}`]: props.theme,
    })
})

const styleObject = computed(() => {
    let result = {}

    if (props.borderTop) result.borderTopWidth = `${props.borderTopWidth}px`
    if (props.borderBottom) result.borderBottomWidth = `${props.borderBottomWidth}px`

    if (props.bgImage) {
        result.backgroundImage = `url(${props.bgImage})`
        result.paddingLeft = `${props.bgIndent}px`
        result.backgroundSize = `auto ${bgImageHeight[props.bgSize]}px`
        result.minHeight = `${bgImageHeight[props.bgSize]}px`
    }

    return result
})

defineExpose({
    props
})
</script>

<style lang="scss" src="./index.scss"></style>
